// Tipos da assinatura digital, compartilhados entre os módulos que assinam
// (Expedientes desde 14/08/2026, Vendas desde 21/08/2026). Espelham
// `assinatura.envelopes` + `assinatura.envelope_signatarios` no banco.

export interface SignatarioEnvio {
  nome: string;
  email: string;
  origem: 'interno' | 'externo';
  usuario_email?: string;      // obrigatório quando interno
  ordem?: number;
  d4sign_act?: '1' | '2' | '5'; // assinar | aprovar | testemunha
  exige_icp?: boolean;
  // Como a pessoa assina NESTE documento (Contratante, Responsável Técnico,
  // Anuente...). Sai impresso sob o nome. Opcional, máx. 60 caracteres.
  // Não confundir com `d4sign_act`, que é o papel na assinatura eletrônica.
  qualificacao?: string;
}

export interface SignatarioStatus {
  nome: string; email: string; ordem: number;
  origem: string; status: 'pendente' | 'assinado' | 'recusado';
  assinado_em: string | null;
  usuario_email?: string | null;
  qualificacao?: string | null;
}

export interface Envelope {
  id: string;
  status: 'rascunho' | 'processando_envio' | 'aguardando_assinatura'
        | 'assinado' | 'cancelado' | 'erro_envio' | 'erro_consulta';
  documento_uuid: string | null;
  enviado_em: string | null;
  assinado_em: string | null;
  cancelado_motivo: string | null;
  pdf_assinado_s3_key: string | null;
  workflow: boolean;
  signatarios: SignatarioStatus[];
  /** Por que o envio falhou, já em português (só em `erro_envio`).
   *  `cota` = a D4 Sign recusou por limite de operações por hora: tentar de
   *  novo agora consome o mesmo limite e atrasa a liberação. */
  falha?: { cota: boolean; texto: string } | null;
  /** Assinado, mas o arquivo assinado ainda não voltou da D4 Sign (o sistema
   *  tenta sozinho 1h depois do último toque). */
  aguardando_arquivo?: boolean;
  /** Quando a próxima tentativa de buscar o arquivo fica elegível (ISO). */
  arquivo_previsto_em?: string | null;
  /** O que vai acontecer SOZINHO com um envio que falhou (só em `erro_envio`).
   *  Sem isto a tela mostra o motivo e cala sobre o desfecho — e quem lê
   *  conclui que precisa reenviar, que é justamente o clique que duplica a
   *  via no cofre da D4 Sign (30/09/2026). */
  retentativa?: {
    /** Quando o sistema tenta de novo (ISO). `null` = não vai tentar. */
    prevista_em: string | null;
    tentativas: number;
    maximo: number;
    /** O DOCUMENTO tem alguma via assinável parada no cofre da D4 Sign —
     *  possivelmente de um envelope mais antigo que este. Reenviar cria uma
     *  segunda via. Um documento junta vários envelopes em erro (o
     *  OF-2026-002 tem cinco), e a pergunta é do documento, não deste. */
    no_cofre: boolean;
    /** ESTE envelope é o que tem a via no cofre. Só ele pode ser RETOMADO:
     *  retomar um que não subiu nada, com a via de um irmão lá dentro,
     *  criaria a segunda via. */
    este_no_cofre: boolean;
    /** Em que passo parou: 'pdf' | 'upload' | 'createlist' | 'sendtosigner'. */
    etapa: string | null;
    /** A máquina parou de tentar — a bola é de uma pessoa. */
    desistiu: boolean;
  } | null;
}

export interface StatusAssinatura {
  envelope: Envelope | null;
  progresso?: { assinados: number; total: number };
}

// "Assina como" sem acento e sem caixa, para comparar ("CONTRATANTE" = "contratante").
export const normalizarQualificacao = (q?: string | null): string =>
  String(q || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Papéis obrigatórios que ainda não estão na lista (venda: Contratante e
 * Contratada; contrato do Jurídico: os rótulos da ficha, que num mútuo são
 * Mutuante e Mutuária). Espelha a trava do servidor
 * (`handlers/papeisAssinatura.js`, achada na VND-2026-079 em 15/09/2026,
 * enviada só com a nossa assinatura): aqui é para a pessoa ver ANTES de clicar;
 * quem recusa de verdade é o servidor.
 *
 * Conta por POSIÇÃO, não por presença: contrato em que os dois lados assinam
 * como "Parte" exige ['Parte','Parte'] e precisa de DOIS signatários "Parte".
 * Só conta quem assina de verdade — testemunha (act 5) e aprovador (2) não são
 * parte, e a testemunha é derivada da própria qualificação no envio.
 */
export function papeisFaltando(
  signatarios: SignatarioEnvio[] | null | undefined, obrigatorios: string[] | null | undefined,
): string[] {
  const disponiveis = new Map<string, number>();
  for (const s of signatarios || []) {
    if (String(s?.d4sign_act || '1') !== '1') continue;
    const chave = normalizarQualificacao(s?.qualificacao);
    if (!chave || chave === 'testemunha') continue;
    disponiveis.set(chave, (disponiveis.get(chave) || 0) + 1);
  }
  const faltam: string[] = [];
  for (const papel of obrigatorios || []) {
    const chave = normalizarQualificacao(papel);
    if (!chave) continue;
    const resta = disponiveis.get(chave) || 0;
    if (resta > 0) disponiveis.set(chave, resta - 1);
    else faltam.push(papel);
  }
  return faltam;
}

// Sugestões do campo "Assina como". Lista aberta — o campo aceita texto livre.
export const QUALIFICACOES = [
  'Contratante', 'Contratada', 'Responsável Técnico', 'Anuente', 'Interveniente',
  'Outorgante', 'Outorgado', 'Representante Legal', 'Diretor', 'Testemunha',
  'Fiscal do Contrato', 'Coordenador do Contrato',
];

// Estados em que o envio ainda está vivo — a UI mostra "verificar" e "cancelar",
// e um novo envio é recusado pelo banco (`uq_env_entidade_viva`).
export const ENVELOPE_VIVO: Envelope['status'][] = ['aguardando_assinatura', 'processando_envio'];

export const ROTULO_STATUS: Record<Envelope['status'], string> = {
  rascunho: 'preparando',
  processando_envio: 'enviando para a D4 Sign…',
  aguardando_assinatura: 'aguardando assinatura',
  assinado: 'assinado',
  cancelado: 'envio cancelado',
  erro_envio: 'o envio falhou',
  erro_consulta: 'não consegui consultar',
};
